import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import {
  CLOUD_STORAGE_CATEGORIES,
  NO_FOLDER_LABEL,
  NO_PATIENT_LABEL,
  type CloudStorageFile,
  type CloudStoragePlan,
  type StorageCategory,
  type StorageCategoryId,
  type StorageClinicUsage,
  type StorageSourceId,
} from '@components/pages/Settings/System/CloudStorage/cloudStorageSettings.constants'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CLOUD_STORAGE')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

/**
 * The business layer (`cloud-storage-module`) already performs the snake→camel
 * shaping and the usage projection math, so these DTOs mirror its public
 * (camelCase) response types. This API layer only adapts those DTOs onto the
 * UI's existing constant types (colors/labels/order live in the UI constants).
 */
export interface CloudStoragePlanDto {
  name: string | null
  monthlyPrice: number | null
  billingFrequency: string | null
  totalGb: number
  startDate: string | null
  endDate: string | null
  renewsOn: string | null
  trialEndDate: string | null
}

export interface CloudStorageUsageDto {
  usedGb: number
  usedBytes: number
  totalGb: number
  totalBytes: number
  usedPercent: number
  growthRateGbPerMonth: number
  growthRateBytesPerMonth: number
  monthsUntilCapacity: number | null
  monthlySeries: Array<{ month: string; usedGb: number }>
}

export interface StorageCategoryUsageDto {
  id: StorageCategoryId
  label: string
  usedGb: number
  usedBytes: number
  fileCount: number
  backed: boolean
}

export interface StorageClinicUsageDto {
  clinicId: string | null
  clinicName: string
  usedGb: number
  usedBytes: number
  fileCount: number
}

export interface CloudStorageSummaryDto {
  plan: CloudStoragePlanDto
  usage: CloudStorageUsageDto
  categories: StorageCategoryUsageDto[]
  clinics: StorageClinicUsageDto[]
}

export interface CloudStorageFileDto {
  fileId: string
  fileName: string
  originalName: string
  fileType: string
  fileCategory: string
  storageCategory: StorageCategoryId
  mimeType: string
  /** Raw size in bytes (BIGINT); formatted to a display string here. */
  fileSize: number
  patientId: string | null
  patientName: string | null
  folderId: string | null
  folderName: string | null
  source: StorageSourceId
  sourceLabel: string
  createdBy: string
  createdByName: string | null
  createdAt: string | null
}

export interface CloudStorageFilesResultDto {
  items: CloudStorageFileDto[]
  total: number
  page: number
  pageSize: number
}

export interface CloudStorageSummary {
  plan: CloudStoragePlan
  categories: StorageCategory[]
  clinics: StorageClinicUsage[]
}

export interface CloudStorageSummaryParams {
  /** Scope usage/categories to the active clinic. */
  clinicId: string
}

export interface CloudStorageFilesParams {
  search?: string
  category?: string
  type?: string
  clinicId: string
  page: number
  pageSize: number
}

export interface CloudStorageFilesResult {
  items: CloudStorageFile[]
  total: number
  page: number
  pageSize: number
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

/** Format a raw byte count into the UI display string (e.g. `"455 MB"`, `"1.5 GB"`). */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B'

  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    BYTE_UNITS.length - 1,
  )
  const value = bytes / 1024 ** exponent
  // Whole numbers show no decimals; otherwise keep one for readability.
  const rounded =
    value >= 10 || Number.isInteger(value)
      ? Math.round(value)
      : Math.round(value * 10) / 10

  return `${rounded} ${BYTE_UNITS[exponent]}`
}

/** Format an ISO date into the plan card's display string (e.g. `"May 15, 2026"`). */
const formatRenewalDate = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const toPlan = (dto: CloudStorageSummaryDto): CloudStoragePlan => ({
  name: dto.plan.name ?? 'Storage plan',
  renewsOn: formatRenewalDate(dto.plan.renewsOn),
  monthlyPrice: dto.plan.monthlyPrice ?? 0,
  usedGb: dto.usage.usedGb,
  totalGb: dto.usage.totalGb,
  usedBytes: dto.usage.usedBytes ?? 0,
  totalBytes: dto.usage.totalBytes ?? 0,
  usedPercent: dto.usage.usedPercent,
  growthRateBytesPerMonth: dto.usage.growthRateBytesPerMonth ?? 0,
  monthsUntilCapacity: dto.usage.monthsUntilCapacity,
})

/**
 * Merge the server's per-category `usedGb`/`fileCount` onto the UI's
 * `CLOUD_STORAGE_CATEGORIES` so color/legend/order metadata stays in the UI.
 * Categories the server omits fall back to `0`.
 */
const toCategories = (dto: CloudStorageSummaryDto): StorageCategory[] => {
  const usageById = new Map(
    dto.categories.map((category) => [category.id, category]),
  )
  return CLOUD_STORAGE_CATEGORIES.map((category) => ({
    ...category,
    usedGb: usageById.get(category.id)?.usedGb ?? 0,
    usedBytes: usageById.get(category.id)?.usedBytes ?? 0,
  }))
}

/** Display label per storage category id, used by the file table's Category column. */
const categoryLabelById = new Map(
  CLOUD_STORAGE_CATEGORIES.map((category) => [category.id, category.label]),
)

/**
 * Map the server's per-clinic usage into the UI breakdown, computing each
 * clinic's share of the tenant-wide used total (so the bars sum to ~100%).
 */
const toClinics = (dto: CloudStorageSummaryDto): StorageClinicUsage[] => {
  const totalUsedGb = dto.clinics.reduce(
    (sum, clinic) => sum + clinic.usedGb,
    0,
  )
  return dto.clinics.map((clinic) => ({
    clinicId: clinic.clinicId,
    clinicName: clinic.clinicName,
    usedGb: clinic.usedGb,
    usedBytes: clinic.usedBytes ?? 0,
    fileCount: clinic.fileCount,
    usedPercent:
      totalUsedGb > 0
        ? Math.round((clinic.usedGb / totalUsedGb) * 1000) / 10
        : 0,
  }))
}

const parseUploadedAt = (value: string | null): number | null => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/** Format an upload timestamp for the file table (e.g. `"07/30/2026 - 10:12 AM"`). */
const formatUploadedAt = (time: number | null): string => {
  if (time === null) return '—'
  const date = new Date(time)
  const day = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
  const clock = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${day} - ${clock}`
}

const toFile = (dto: CloudStorageFileDto): CloudStorageFile => {
  const uploadedAtTime = parseUploadedAt(dto.createdAt)
  return {
    id: dto.fileId,
    fileName: dto.fileName || dto.originalName,
    type: dto.fileType,
    categoryId: dto.storageCategory,
    categoryLabel: categoryLabelById.get(dto.storageCategory) ?? 'Other',
    patient: dto.patientName?.trim() || NO_PATIENT_LABEL,
    fileSize: formatFileSize(dto.fileSize),
    fileSizeBytes: dto.fileSize ?? 0,
    source: dto.source,
    sourceLabel: dto.sourceLabel,
    location: dto.folderName ?? NO_FOLDER_LABEL,
    uploadedBy: dto.createdByName ?? '—',
    uploadedAt: formatUploadedAt(uploadedAtTime),
    uploadedAtTime,
  }
}

export const getCloudStorageSummary = (
  params: CloudStorageSummaryParams,
): Promise<CloudStorageSummary> =>
  axios
    .get<
      ApiResponse<{ cloud_storage: CloudStorageSummaryDto }>
    >(`${BUSINESS_LAYER_API}/api/v1/cloud-storage/summary`, { params })
    .then((response) => {
      const summary = response.data.data.cloud_storage
      return {
        plan: toPlan(summary),
        categories: toCategories(summary),
        clinics: toClinics(summary),
      }
    })

export const getCloudStorageFiles = (
  params: CloudStorageFilesParams,
): Promise<CloudStorageFilesResult> =>
  axios
    .get<
      ApiResponse<CloudStorageFilesResultDto>
    >(`${BUSINESS_LAYER_API}/api/v1/cloud-storage/files`, { params })
    .then((response) => {
      const result = response.data.data
      return {
        items: result.items.map(toFile),
        total: result.total,
        page: result.page,
        pageSize: result.pageSize,
      }
    })
